/**
 * Shared doctors: core's shared provider directory (core_physicians), which
 * every clinic whose provider_sources.physicians isn't local_only routes from.
 *
 * For super admins and anyone with the canManageSharedPhysicians grant. The
 * doctors list, map and detail pages are the same components a clinic's
 * Physicians page uses (in shared mode), so a clinic routing from the
 * directory shows exactly this. Pay lives only here, on its own tab. Edits are
 * drafts (useSharedDraftStore, never the selected clinic's) published as
 * releases of the directory; a release can be reverted. Core applies each
 * change as a whole-document replacement, so every draft carries the full record.
 */

import { format } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Button, Badge, Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui'
import SharedDraftBar from '../components/SharedDraftBar'
import CompensationEditor, { compLabel } from '../components/CompensationEditor'
import { PhysiciansDirectory } from './PhysiciansPage'
import { sharedPhysiciansAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { useSharedDraftStore, SHARED_DIRECTORY_SCOPE } from '../stores/draftStore'

const EXPIRING_DAYS = 60
const today = () => format(new Date(), 'yyyy-MM-dd')
const addDays = (n) => format(new Date(Date.now() + n * 864e5), 'yyyy-MM-dd')
const fullName = (d) => `${d.first_name || ''} ${d.last_name || ''}`.trim()
const errorMessage = (err) => err?.response?.data?.error?.message || err?.message || 'Something went wrong'

/** Live records with pending drafts laid over them; deletes stay listed, flagged. */
function applyDrafts(live, changes) {
  const byId = new Map(live.map(d => [d.id, { ...d }]))
  const pending = new Map()
  const created = []
  for (const c of changes) {
    if (c.operation === 'create') created.push({ ...c.new_data, id: `draft:${c.id}`, _draft: 'create', _changeId: c.id })
    else if (byId.has(c.resource_id)) {
      if (c.operation === 'update') byId.set(c.resource_id, { ...c.new_data, id: c.resource_id })
      pending.set(c.resource_id, c.operation)
    }
  }
  return [...byId.values()].map(d => (pending.has(d.id) ? { ...d, _draft: pending.get(d.id) } : d)).concat(created)
}


function licenseStatus(entry) {
  const exp = entry?.expiry
  if (!entry?.license) return null
  if (!exp) return { label: 'no expiry', tone: 'secondary' }
  if (exp < today()) return { label: 'expired', tone: 'destructive' }
  if (exp <= addDays(EXPIRING_DAYS)) return { label: 'expiring', tone: 'warning' }
  return { label: 'active', tone: 'success' }
}

function activeStates(d) {
  return Object.entries(d.licenses || {}).filter(([, l]) => licenseStatus(l?.state)?.label === 'active' || licenseStatus(l?.state)?.label === 'expiring')
}



const JURISDICTIONS = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY']

const accepting = (d) => d.accept_new_patients || d.accept_refill_patients

/**
 * The to-do list for whoever keeps the directory current: licenses that have
 * lapsed or are about to (on doctors who are taking patients), licenses core
 * can't route on, unknown pay, and states no accepting doctor covers.
 */
function attentionItems(records, canEdit) {
  const items = []
  for (const d of records.filter(r => accepting(r) && r._draft !== 'delete')) {
    for (const [code, l] of Object.entries(d.licenses || {})) {
      for (const [type, label] of [['state', 'license'], ['DEA', 'DEA'], ['CSR', 'CSR']]) {
        const s = licenseStatus(l?.[type])
        if (!s) continue
        if (s.label === 'expired') items.push({ level: 0, record: d, text: `${code} ${label} expired ${l[type].expiry}` })
        else if (s.label === 'expiring') items.push({ level: 1, record: d, text: `${code} ${label} expires ${l[type].expiry}` })
        else if (s.label === 'no expiry' && type === 'state') items.push({ level: 1, record: d, text: `${code} license has no expiry, so core won't route on it` })
      }
    }
    if (canEdit && compLabel(d.compensation) === 'unknown') items.push({ level: 2, record: d, text: 'pay unknown, so routing ranks them last' })
  }
  const covered = new Set(records.filter(r => r.accept_new_patients && r._draft !== 'delete').flatMap(r => activeStates(r).map(([s]) => s)))
  const gaps = JURISDICTIONS.filter(s => !covered.has(s))
  return { items: items.sort((a, b) => a.level - b.level || (a.text < b.text ? -1 : 1)), gaps }
}

function NeedsAttention({ records, canEdit, onOpen }) {
  const { items, gaps } = useMemo(() => attentionItems(records, canEdit), [records, canEdit])
  const [all, setAll] = useState(false)
  if (!items.length && !gaps.length) return null
  const tone = ['text-red-700', 'text-amber-700', 'text-muted-foreground']
  const shown = all ? items : items.slice(0, 8)
  return (
    <Card className="space-y-2 p-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Needs attention <span className="font-normal text-muted-foreground">({items.length})</span></h2>
        {items.length > 8 && <Button size="sm" variant="ghost" onClick={() => setAll(a => !a)}>{all ? 'Show fewer' : `Show all ${items.length}`}</Button>}
      </div>
      {gaps.length > 0 && (
        <p className="text-sm"><span className="font-medium text-red-700">No doctor taking new patients in:</span> {gaps.join(', ')}</p>
      )}
      <ul className="grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
        {shown.map((it, i) => (
          <li key={i}>
            <button type="button" className="text-left hover:underline" onClick={() => onOpen(it.record.id)}>
              <span className="font-medium">{fullName(it.record)}</span> <span className={tone[it.level]}>{it.text}</span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function PayTab({ records, onDraft }) {
  const [editing, setEditing] = useState(null)
  const rows = records.filter(r => r._draft !== 'create' && r._draft !== 'delete').sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">Pay is shown only on this page, never on a clinic's Physicians page. Routing prefers salary, then the lowest consult rate, then unknown.</p>
      <div className="overflow-x-auto rounded border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left"><tr><th className="p-2">Doctor</th><th className="p-2">Accepting</th><th className="p-2">Pay</th><th className="p-2" /></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t align-top">
                <td className="p-2 font-medium">{fullName(r)}, {r.title} {r._draft && <Badge variant="outline" className="ml-1">draft</Badge>}</td>
                <td className="p-2">{r.accept_new_patients ? 'new' : '—'}{r.accept_refill_patients ? ' · refill' : ''}</td>
                <td className="p-2">
                  {editing === r.id
                    ? <CompensationEditor record={r} onSave={(comp) => {
                        const next = { ...r }
                        delete next._draft
                        if (comp) next.compensation = comp; else delete next.compensation
                        onDraft({ resource_type: 'physician', resource_id: r.id, operation: 'update', original_data: r, new_data: next, description: `Pay for ${fullName(r)}: ${compLabel(comp)}` })
                        setEditing(null)
                      }} />
                    : compLabel(r.compensation)}
                </td>
                <td className="p-2 text-right">{editing !== r.id && <Button size="sm" variant="ghost" onClick={() => setEditing(r.id)}>Edit</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ReleasesTab({ refreshKey, onReverted }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [report, setReport] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(() => {
    sharedPhysiciansAPI.listReleases({ limit: 50 })
      .then(r => setData(r.data.data))
      .catch(err => setError(errorMessage(err)))
  }, [])
  useEffect(load, [load, refreshKey])

  const revert = async (rel) => {
    if (!confirm(`Restore the directory to how it was before Release #${rel.release_number} ("${rel.release_name}")? This creates a new release. Doctors added since are deleted, or disabled if core still references them.`)) return
    setBusyId(rel._id)
    try {
      const r = await sharedPhysiciansAPI.revert(rel._id)
      setReport(r.data.data.report)
      load(); onReverted()
    } catch (err) {
      alert(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  if (error) return <p className="text-sm text-red-700">{error}</p>
  if (!data) return <p className="text-sm text-muted-foreground">Loading releases…</p>
  return (
    <div className="space-y-3">
      {report && (
        <Card className="p-3 text-sm">
          <div className="font-medium">Revert applied</div>
          <div className="text-muted-foreground">
            {report.updated.length} restored · {report.recreated.length} re-created · {report.deleted.length} deleted
            {report.disabled.length > 0 && <> · {report.disabled.length} disabled instead of deleted ({report.disabled.map(d => `${d.name}: ${d.reason}`).join('; ')})</>}
          </div>
        </Card>
      )}
      {data.releases.length === 0 && <p className="text-sm text-muted-foreground">No releases yet. The directory was seeded directly; the first publish takes its first snapshot.</p>}
      {data.releases.map(rel => (
        <Card key={rel._id} className="flex items-center gap-3 p-3">
          <div className="w-14 text-sm font-semibold">#{rel.release_number}</div>
          <div className="min-w-0 flex-1">
            <div className="font-medium">{rel.release_name} {rel.is_active && <Badge className="ml-1">active</Badge>} {rel.revert_of && <Badge variant="outline" className="ml-1">revert</Badge>}</div>
            <div className="truncate text-xs text-muted-foreground">
              {rel.created_by?.name} · {new Date(rel.published_at || rel.created_at).toLocaleString()}{rel.description ? ` · ${rel.description}` : ''}
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busyId === rel._id} onClick={() => revert(rel)}>
            {busyId === rel._id ? 'Reverting…' : 'Restore pre-release state'}
          </Button>
        </Card>
      ))}
    </div>
  )
}

function ClinicsTab() {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    sharedPhysiciansAPI.sources().then(r => setRows(r.data.data)).catch(err => setError(errorMessage(err)))
  }, [])
  if (error) return <p className="text-sm text-red-700">{error}</p>
  if (!rows) return <p className="text-sm text-muted-foreground">Loading clinics…</p>
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        Which clinics route from this directory. <code>local_only</code> uses the clinic's own doctors; <code>admin_only</code> uses only this directory;
        <code> admin_with_overrides</code> uses the directory plus linked clinic copies. Switching a clinic is done in core.
      </p>
      <div className="overflow-x-auto rounded border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left"><tr><th className="p-2">Clinic</th><th className="p-2">Core name</th><th className="p-2">Physicians</th><th className="p-2">Pharmacies</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.clinic} className="border-t">
                <td className="p-2">{r.name}</td>
                <td className="p-2 font-mono text-xs">{r.core_name}</td>
                {r.sources
                  ? <><td className="p-2"><Badge variant={r.sources.physicians === 'local_only' ? 'secondary' : 'default'}>{r.sources.physicians}</Badge></td><td className="p-2"><Badge variant="secondary">{r.sources.pharmacies}</Badge></td></>
                  : <td className="p-2 text-muted-foreground" colSpan={2}>{r.error}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** The Shared Doctors page: super admins and holders of the grant. */
export default function SharedPhysiciansPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const canEdit = user?.role === 'super_admin' || Boolean(user?.permissions?.canManageSharedPhysicians)
  const { changes, initialize, addChange } = useSharedDraftStore()
  const [live, setLive] = useState(null)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => { if (user?.id) initialize(SHARED_DIRECTORY_SCOPE, user.id) }, [user?.id, initialize])
  const load = useCallback(() => {
    setError(null)
    sharedPhysiciansAPI.list().then(r => setLive(r.data.data)).catch(err => setError(errorMessage(err)))
    setReloadKey(k => k + 1)
  }, [])
  useEffect(() => { if (canEdit) load() }, [canEdit, load])

  const records = useMemo(() => (live ? applyDrafts(live, changes) : []), [live, changes])

  if (user && !canEdit) {
    return <div className="p-6 text-sm text-muted-foreground">You don't have access to the shared doctor directory. A super admin can grant it.</div>
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Shared doctors</h1>
        <p className="text-sm text-muted-foreground">
          Core's shared provider directory. Changes are drafted, then published as a release that applies to every clinic routing from it.
        </p>
      </div>

      <SharedDraftBar onPublished={load} />
      {error && <p className="text-sm text-red-700">{error}</p>}
      {live && <NeedsAttention records={records} canEdit onOpen={(id) => navigate(`/shared-doctors/${id}`)} />}

      <Tabs defaultValue="doctors">
        <TabsList>
          <TabsTrigger value="doctors">Doctors</TabsTrigger>
          <TabsTrigger value="pay">Pay</TabsTrigger>
          <TabsTrigger value="releases">Releases</TabsTrigger>
          <TabsTrigger value="clinics">Clinics</TabsTrigger>
        </TabsList>
        <TabsContent value="doctors">
          <PhysiciansDirectory key={reloadKey} shared canEdit basePath="/shared-doctors" title="Doctors" bare />
        </TabsContent>
        <TabsContent value="pay">{live && <PayTab records={records} onDraft={addChange} />}</TabsContent>
        <TabsContent value="releases"><ReleasesTab refreshKey={reloadKey} onReverted={load} /></TabsContent>
        <TabsContent value="clinics"><ClinicsTab /></TabsContent>
      </Tabs>
    </div>
  )
}
